// DS: CR18S
// @dynamic-i18n-keys
// collectionPanel.notification.addedToCollection.v2
// collectionPanel.notification.addedToCollection.multi.v2
// collectionPanel.notification.removedFromCollection.v2
// collectionPanel.notification.removedFromCollection.multi.v2
// collectionPanel.notification.removedFromAllCollections.v2
// collectionPanel.notification.miscChanges.v2
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { PapiGallection, PapiUser } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { NotificationType } from '../../Notifications/redux/types';
import Notification from '../../Notifications/Notification';
import SystemNotification from '../../SystemNotification';
import { CollectNotification as CollectNotificationType, ChangeType } from '../redux/types';
import AddToFaveIcon from '@wix/da-ds/pkg/Icons/24x24/AddedToFav';
import RemoveFaveIcon from '@wix/da-ds/pkg/Icons/24x24/RemovedFromFav';
import CheckIcon from '@wix/da-ds/pkg/Icons/Check';
import { getDeviationNotificationQueue } from '../../DeviationPage/redux/helpers';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { ResourceType } from '../../types/resource';
import { GallectionResourceType } from '../../types/gallection';
import { BiContextProvidedValue, BiData, ItemClickBiEvent } from '@wix/da-bi/pkg/events';

export type Params = CollectNotificationType['params'] & Omit<CollectNotificationType, 'params'>;

type keyMap = { [k in ChangeType]: string };

const keys: keyMap = {
  addOne: 'collectionPanel.notification.addedToCollection.v2',
  addMany: 'collectionPanel.notification.addedToCollection.multi.v2',
  removeOne: 'collectionPanel.notification.removedFromCollection.v2',
  removeSome: 'collectionPanel.notification.removedFromCollection.multi.v2',
  removeAll: 'collectionPanel.notification.removedFromAllCollections.v2',
  misc: 'collectionPanel.notification.miscChanges.v2',
  none: '',
};

const undoMap: { [k in ChangeType]: ChangeType } = {
  removeOne: 'addOne',
  removeSome: 'addMany',
  removeAll: 'addMany',
  addOne: 'removeOne',
  addMany: 'removeSome',
  misc: 'misc',
  none: 'none',
};

export interface Props extends Params {
  exampleCollection?: PapiGallection;
  currentUser?: PapiUser;
  onUndo: (deviationId: number) => void;
  addNotification: (queueName: string, type: string | NotificationType, params: any) => void;
  removeNotification: (id: number) => void;
}

export const SCROLL_TO_FOLDER_HASH_SIGNAL = 'scrollIntoView';

export class CollectNotification extends React.Component<Props & WithTranslation> {
  render() {
    const { id, collectedDeviation, queueName, addNotification } = this.props;

    return (
      <Notification
        id={id}
        queueName={queueName}
        cardComponent={SystemNotification}
        cardProps={
          this.isPrivate()
            ? {}
            : {
                iconComponent: this.getIcon(),
                backgroundDeviation: collectedDeviation,
              }
        }
        onClose={
          queueName === getDeviationNotificationQueue(collectedDeviation) && !this.isUndo()
            ? () => addNotification(queueName, 'commentReminder', {})
            : undefined
        }
      >
        {this.renderTitle()}
      </Notification>
    );
  }

  isPrivate = () => {
    return this.props.type === NotificationType.CollectPrivate;
  };

  getIcon() {
    const { changeType } = this.props;
    switch (changeType) {
      case 'removeAll':
      case 'removeOne':
      case 'removeSome':
        return RemoveFaveIcon;
      case 'addMany':
      case 'addOne':
        return AddToFaveIcon;
      default:
        return CheckIcon;
    }
  }

  renderTitle() {
    const { changeType, changedFolderCount, t, exampleCollection, currentUser } = this.props;
    const effectiveChange = this.isUndo() ? undoMap[changeType] : changeType;
    const key = keys[effectiveChange];

    if (!key || !currentUser) {
      return null;
    }

    switch (effectiveChange) {
      case 'addOne':
      case 'removeOne':
        const collection = exampleCollection
          ? exampleCollection.name
          : t('collectionPanel.aCollection');
        const url = exampleCollection
          ? Url.userGalleryLink({
              username: currentUser.username,
              gallectionType: exampleCollection.type,
              folderId: exampleCollection.folderId,
              folderName: exampleCollection.name,
            })
          : Url.userGalleryLink({
              username: currentUser.username,
              gallectionType: 'collection',
            });
        const biData = BiData<ItemClickBiEvent>({
          evid: 100,
          itemid: exampleCollection?.folderId ?? currentUser.userId,
          typeid: exampleCollection
            ? this.isPrivate()
              ? GallectionResourceType.PRIVATE_COLLECTION
              : GallectionResourceType.COLLECTION
            : ResourceType.USER,
          sectionid: BiContextProvidedValue,
          sectionname: BiContextProvidedValue,
          section_typeid: BiContextProvidedValue,
          sectiontype: BiContextProvidedValue,
          widgetname: BiContextProvidedValue,
        });

        return t(key, {
          postProcess: ['reactPostprocessor', 'featureBrancher'],
          collection: (
            <BiLink biData={biData} href={`${url}#${SCROLL_TO_FOLDER_HASH_SIGNAL}`}>
              {collection}
            </BiLink>
          ),
        });
      default:
        return t(key, { changedFolderCount });
    }
  }

  isUndo() {
    return this.props.type === NotificationType.CollectUndo;
  }

  onUndo = () => {
    const {
      collectedDeviation: { deviationId },
      onUndo,
      removeNotification,
      id,
    } = this.props;

    onUndo(deviationId);
    removeNotification(id);
  };
}

export default withTranslation()(CollectNotification) as React.ComponentType<Props>;
